import { useQuery } from '@tanstack/react-query';
import { Contentful, Network } from '@wren/shared';
import { ProjectRegistryData } from '@wren/shared/src/lib/isometric';
import first from 'lodash/first';
import { HTMLAttributes } from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect } from 'react-router-dom';
import useContentful from '../../hooks/useContentful';
import SkeletonLine from '../UI/SkeletonLine';
import ProjectPageRichText from './ProjectPageRichText';
import CertificationCallout from './ProjectPageSections/CertificationCallout';
import { ContactCallout } from './ProjectPageSections/ContactCallout';
import CreditTimeline from './ProjectPageSections/CreditTimeline';
import KitaCallout from './ProjectPageSections/KitaCallout';
import ProjectDocumentation from './ProjectPageSections/ProjectDocumentation';
import ReceiptsAndDocuments from './ProjectPageSections/ReceiptsAndDocuments';
import { projectSubHeader } from './ProjectRenderer';
import UpdatesTimeline from './UpdatesTimeline';

async function getRegistryData(slug: string) {
  const [response, responseBody] = await Network.get<ProjectRegistryData>(
    `registry`,
    { slug }
  );

  if (response.ok && responseBody) {
    return responseBody;
  } else {
    throw new Error('Error getting project registry data');
  }
}

export default function ContentfulProjectPage({
  slug,
  ...restOfProps
}: {
  slug: string /* aka link */;
} & HTMLAttributes<HTMLDivElement>) {
  const { client } = useContentful();
  const pageResult = useQuery({
    queryKey: ['projectPage', slug],
    queryFn: () => Contentful.getProjectPage(slug, client),
    staleTime: 300000, // 5 minutes
  });

  const registryResult = useQuery({
    queryKey: ['registryData', slug],
    queryFn: () => getRegistryData(slug),
    staleTime: 300000, // 5 minutes
  });

  const registryData = registryResult.data;

  if (!pageResult.isFetched) {
    return <SkeletonLine />;
  }

  const page = first(pageResult.data?.items);

  if (!page) {
    return <Redirect to="/404" />;
  }

  const {
    title,
    content,
    howDoesItWork,
    why,
    press,
    location,
    partners,
    financials,
    vetting,
    wrenCertification,
    verification,
    crediting,
    contactInformation,
    archiveDate,
    archiveReason,
  } = page.fields;

  return (
    <>
      <Helmet>
        <title>Wren | {page?.fields.title}</title>
        <meta
          property="og:title"
          content="Wren - Offset your carbon footprint"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://www.wren.co/projects/${slug}`}
        />
        <meta property="og:image" content="https://i.imgur.com/7tarhAU.jpg" />
        <meta
          property="og:description"
          content={`Offset your carbon footprint by supporting ${title}.`}
        />
      </Helmet>
      <div>
        {archiveReason && (
          <p>
            <em>
              {archiveDate &&
                new Date(archiveDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                })}
              {': '}
              {archiveReason}
            </em>
          </p>
        )}
        {content && (
          <ProjectPageRichText
            document={content}
            slug={slug}
            {...restOfProps}
          />
        )}
        {howDoesItWork && (
          <>
            <h2 css={projectSubHeader} id="how-does-it-work">
              How does it work?
            </h2>
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={howDoesItWork}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {why && (
          <>
            <h2 css={projectSubHeader} id="why-this-project">
              Why this project?
            </h2>
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={why}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {press && (
          <>
            <h2 css={projectSubHeader} id="press-mentions">
              Press mentions
            </h2>
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={press}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        <UpdatesTimeline slug={slug} hasTitle />
        {location && (
          <>
            <h2 css={projectSubHeader} id="location">
              Where is the project located?
            </h2>
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={location}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {partners && (
          <>
            <h2 css={projectSubHeader} id="partners">
              Who is behind this project?
            </h2>
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={partners}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {financials && (
          <>
            <h2 css={projectSubHeader} id="financials">
              How is the money spent?
            </h2>
            <ReceiptsAndDocuments slug={slug} />
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={financials}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {vetting && (
          <>
            <h2 css={projectSubHeader} id="vetting">
              How did we vet this project?
            </h2>
            {wrenCertification && (
              <CertificationCallout
                certificationCategory={wrenCertification}
                projectName={title}
              />
            )}
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={vetting}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {verification && (
          <>
            <h2 css={projectSubHeader} id="verification">
              Verification
            </h2>
            <ProjectPageRichText
              style={{ marginTop: '0' }}
              document={verification}
              slug={slug}
              {...restOfProps}
            />
          </>
        )}
        {crediting?.includes('Kita') && <KitaCallout />}
        {crediting?.includes('Isometric') && registryData && (
          <>
            {registryData.creditTimeline?.length > 0 && (
              <CreditTimeline registryData={registryData} />
            )}
            {registryData.documents.length > 0 && (
              <ProjectDocumentation registryData={registryData} />
            )}
          </>
        )}
        <h2 css={projectSubHeader} id="contact">
          Contact information
        </h2>
        {contactInformation && (
          <ProjectPageRichText
            style={{ marginTop: '0' }}
            document={contactInformation}
            slug={slug}
            {...restOfProps}
          />
        )}
        <ContactCallout title={title.trim()} />
      </div>
    </>
  );
}
